import { createFileRoute, redirect, Link, useNavigate } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DollarSign,
  Plus,
  TrendingUp,
  TrendingDown,
  Building2,
  CreditCard,
  Wallet,
  Smartphone,
  ArrowUpRight,
  ArrowDownRight,
  AlertCircle,
  Loader2,
  ArrowRight,
  Receipt,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import {
  useAccounts,
  useTransactions,
  useAccountSummary,
  useTransactionSummary,
  useExpiringChecks,
} from '@/hooks/queries/useTreasury';
import { AccountForm } from '@/features/treasury/components/AccountForm';
import { TransactionForm } from '@/features/treasury/components/TransactionForm';
import { TransferForm } from '@/features/treasury/components/TransferForm';
import { useState } from 'react';

export const Route = createFileRoute('/apps/treasury/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: TreasuryDashboard,
});

function TreasuryDashboard() {
  const navigate = useNavigate();
  const { company } = useAuthStore();
  const { data: accounts = [], isLoading: accountsLoading, refetch: refetchAccounts } = useAccounts(company?.id || '');
  const { data: transactions = [], isLoading: transactionsLoading, refetch: refetchTransactions } = useTransactions(company?.id || '');
  const summary = useAccountSummary(company?.id || '');
  const transactionSummary = useTransactionSummary(company?.id || '', 'month');
  const { data: expiringChecks = [] } = useExpiringChecks(company?.id || '');
  
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [showTransferForm, setShowTransferForm] = useState(false);

  const handleRefresh = () => {
    refetchAccounts();
    refetchTransactions();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-DZ', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  if (accountsLoading || transactionsLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              الخزينة / Trésorerie
            </h1>
            <p className="mt-2 text-gray-600">
              إدارة الحسابات والمعاملات المالية / Gérez vos comptes et transactions
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleRefresh}
            >
              تحديث / Actualiser
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate({ to: '/apps/treasury/accounts' })}
            >
              <CreditCard className="w-4 h-4 mr-2" />
              الحسابات / Comptes
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate({ to: '/apps/treasury/transfers' })}
              className="border-purple-200 text-purple-700 hover:bg-purple-50"
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Historique transferts
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowTransferForm(true)}
              className="border-purple-200 text-purple-700 hover:bg-purple-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              تحويل / Transfert
            </Button>
            <Button
              onClick={() => setShowTransactionForm(true)}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              معاملة جديدة / Nouvelle
            </Button>
          </div>
        </div>

        {/* Expiring Checks Alert */}
        {expiringChecks.length > 0 && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-orange-600" />
                <div>
                  <p className="font-medium text-orange-900">
                    {expiringChecks.length} شيك قريب من الانتهاء / chèque(s) expirant bientôt
                  </p>
                  <p className="text-sm text-orange-700">
                    الشيكات صالحة لمدة 180 يوماً في الجزائر / Les chèques sont valables 180 jours en Algérie
                  </p>
                </div>
                <Button variant="outline" size="sm" className="ml-auto border-orange-300">
                  عرض / Voir
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Total Balance */}
        <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
          <CardContent className="p-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm mb-2">
                  الرصيد الإجمالي / Solde Total
                </p>
                <p className="text-5xl font-bold mb-4">{formatCurrency(summary.total)}</p>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <ArrowUpRight className="w-4 h-4" />
                    <span>الدخل / Entrées: {formatCurrency(transactionSummary.income)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <ArrowDownRight className="w-4 h-4" />
                    <span>المصروفات / Sorties: {formatCurrency(transactionSummary.expense)}</span>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                <DollarSign className="w-12 h-12" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Accounts by Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Bank Accounts */}
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setShowAccountForm(true)}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-blue-50 rounded-xl">
                  <Building2 className="w-8 h-8 text-blue-600" />
                </div>
                <span className="text-2xl font-bold text-gray-900">
                  {summary.accounts.bank}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-2">البنوك / Banques</p>
              <p className="text-xl font-semibold text-gray-900">
                {formatCurrency(summary.byType.bank)}
              </p>
            </CardContent>
          </Card>

          {/* CCP Accounts */}
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setShowAccountForm(true)}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-green-50 rounded-xl">
                  <CreditCard className="w-8 h-8 text-green-600" />
                </div>
                <span className="text-2xl font-bold text-gray-900">
                  {summary.accounts.ccp}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-2">CCP</p>
              <p className="text-xl font-semibold text-gray-900">
                {formatCurrency(summary.byType.ccp)}
              </p>
            </CardContent>
          </Card>

          {/* Cash Accounts */}
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setShowAccountForm(true)}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-purple-50 rounded-xl">
                  <Wallet className="w-8 h-8 text-purple-600" />
                </div>
                <span className="text-2xl font-bold text-gray-900">
                  {summary.accounts.cash}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-2">النقدية / Caisse</p>
              <p className="text-xl font-semibold text-gray-900">
                {formatCurrency(summary.byType.cash)}
              </p>
            </CardContent>
          </Card>

          {/* Mobile Money */}
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setShowAccountForm(true)}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-orange-50 rounded-xl">
                  <Smartphone className="w-8 h-8 text-orange-600" />
                </div>
                <span className="text-2xl font-bold text-gray-900">
                  {summary.accounts.mobile}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-2">Mobile Money</p>
              <p className="text-xl font-semibold text-gray-900">
                {formatCurrency(summary.byType.mobile)}
              </p>
            </CardContent>
          </Card>

          {/* Checks - Direct Link */}
          <Card 
            className="hover:shadow-lg transition-all cursor-pointer border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50"
            onClick={() => navigate({ to: '/apps/treasury/checks' })}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-teal-100 rounded-xl">
                  <Receipt className="w-8 h-8 text-teal-600" />
                </div>
                <ArrowRight className="w-5 h-5 text-teal-600" />
              </div>
              <p className="text-sm text-gray-600 mb-2">الشيكات / Chèques</p>
              <p className="text-lg font-semibold text-teal-900">
                Gestion 180j
              </p>
              {expiringChecks.length > 0 && (
                <p className="text-xs text-orange-600 mt-1 font-medium">
                  {expiringChecks.length} expirant
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Transactions */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <CardTitle>المعاملات الأخيرة / Transactions Récentes</CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate({ to: '/apps/treasury/transactions' })}
              >
                عرض الكل / Voir tout →
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {transactions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="font-medium">لا توجد معاملات بعد / Aucune transaction encore</p>
                <p className="text-sm mt-1">ابدأ بإنشاء معاملة جديدة / Commencez par créer une transaction</p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.slice(0, 10).map((transaction) => {
                  const account = accounts.find((a) => a.id === transaction.account_id);
                  
                  return (
                    <div
                      key={transaction.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`p-2 rounded-lg ${
                            transaction.type === 'income'
                              ? 'bg-emerald-100'
                              : 'bg-red-100'
                          }`}
                        >
                          {transaction.type === 'income' ? (
                            <TrendingUp className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <TrendingDown className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {transaction.description}
                          </p>
                          <div className="flex items-center gap-3 mt-1">
                            <p className="text-sm text-gray-500">
                              {account?.name || 'Compte supprimé'}
                            </p>
                            <span className="text-gray-300">•</span>
                            <p className="text-sm text-gray-500">
                              {formatDate(transaction.transaction_date)}
                            </p>
                            {transaction.payment_method && (
                              <>
                                <span className="text-gray-300">•</span>
                                <p className="text-sm text-gray-500 capitalize">
                                  {transaction.payment_method}
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <p
                        className={`text-lg font-semibold ${
                          transaction.type === 'income'
                            ? 'text-emerald-600'
                            : 'text-red-600'
                        }`}
                      >
                        {transaction.type === 'income' ? '+' : '-'}
                        {formatCurrency(transaction.amount)}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        {accounts.length === 0 && (
          <Card className="border-2 border-dashed border-gray-300">
            <CardContent className="p-8 text-center">
              <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                ابدأ بإنشاء حساب / Commencez par créer un compte
              </h3>
              <p className="text-gray-600 mb-6">
                أضف حسابك البنكي، CCP، صندوق نقدي أو محفظة إلكترونية
              </p>
              <Button
                onClick={() => setShowAccountForm(true)}
                className="bg-gradient-to-r from-emerald-500 to-teal-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                إضافة حساب / Ajouter un compte
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Account Form Dialog */}
        <AccountForm
          open={showAccountForm}
          onClose={() => {
            setShowAccountForm(false);
            handleRefresh();
          }}
        />

        {/* Transaction Form Dialog */}
        <TransactionForm
          open={showTransactionForm}
          onClose={() => {
            setShowTransactionForm(false);
            handleRefresh();
          }}
        />

        {/* Transfer Form Dialog */}
        <TransferForm
          open={showTransferForm}
          onClose={() => {
            setShowTransferForm(false);
            handleRefresh();
          }}
        />
      </div>
    </DashboardLayout>
  );
}
